import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { AUTH } from "@/constants/testIds";
import { COUNTRY_LABELS } from "@/lib/api";
import { toast } from "sonner";
import { GoogleAuthButton } from "@/components/GoogleAuthButton";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "particulier",
    country: "MA",
    company: "",
    phone: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const payload = { ...form };
    if (payload.role !== "pro") payload.company = undefined;
    const res = await register(payload);
    setBusy(false);
    if (res.ok) {
      toast.success("Compte créé !");
      navigate("/dashboard");
    } else {
      setError(res.error);
    }
  };

  return (
    <Layout withFooter={false}>
      <div className="max-w-md mx-auto px-6 py-12">
        <span className="text-xs tracking-[0.2em] uppercase text-[#11783A] font-medium">Inscription</span>
        <h1 className="font-heading text-4xl font-medium tracking-tight mt-2 mb-2">Rejoignez 3aqarate</h1>
        <p className="text-muted-foreground mb-8">Publiez vos annonces en quelques minutes.</p>

        <form onSubmit={submit} className="bg-white border border-[#E5E5E5] rounded-2xl p-6 space-y-4">
          <div>
            <Label>Type de compte</Label>
            <Select value={form.role} onValueChange={(v) => update("role", v)}>
              <SelectTrigger data-testid={AUTH.registerRole} className="mt-1 h-11 rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="particulier">Particulier</SelectItem>
                <SelectItem value="pro">Professionnel / Agence</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Nom complet</Label>
            <Input
              data-testid={AUTH.registerName}
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              required
              className="mt-1 rounded-xl h-11"
            />
          </div>

          {form.role === "pro" && (
            <div>
              <Label>Nom de l'agence</Label>
              <Input
                data-testid={AUTH.registerCompany}
                value={form.company}
                onChange={(e) => update("company", e.target.value)}
                className="mt-1 rounded-xl h-11"
              />
            </div>
          )}

          <div>
            <Label>Pays</Label>
            <Select value={form.country} onValueChange={(v) => update("country", v)}>
              <SelectTrigger data-testid={AUTH.registerCountry} className="mt-1 h-11 rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(COUNTRY_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Email</Label>
            <Input
              type="email"
              data-testid={AUTH.registerEmail}
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              required
              className="mt-1 rounded-xl h-11"
            />
          </div>

          <div>
            <Label>Mot de passe (6+ caractères)</Label>
            <Input
              type="password"
              data-testid={AUTH.registerPassword}
              value={form.password}
              onChange={(e) => update("password", e.target.value)}
              required
              minLength={6}
              className="mt-1 rounded-xl h-11"
            />
          </div>

          {error && <div className="text-sm text-red-600">{error}</div>}

          <Button
            type="submit"
            data-testid={AUTH.registerSubmit}
            disabled={busy}
            className="w-full h-11 rounded-xl bg-[#D6202C] hover:bg-[#B5181F]"
          >
            {busy ? "Création..." : "Créer mon compte"}
          </Button>

          <div className="flex items-center gap-3 py-1">
            <div className="flex-1 h-px bg-[#E5E5E5]" />
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">ou</span>
            <div className="flex-1 h-px bg-[#E5E5E5]" />
          </div>
          <GoogleAuthButton label="S'inscrire avec Google" />

          <div className="text-sm text-muted-foreground text-center pt-2">
            Déjà inscrit ? <Link to="/login" className="text-[#D6202C] font-medium">Connexion</Link>
          </div>
        </form>
      </div>
    </Layout>
  );
}
